import type { Coding, Extension, MedicationRequest } from '@medplum/fhirtypes';
import {
  DME_MASK_EXTENSION_URL,
  DME_MASK_TYPE_LABELS,
  DME_PRESSURE_EPAP_URL,
  DME_PRESSURE_EXTENSION_URL,
  DME_PRESSURE_IPAP_URL,
  DME_PRESSURE_VALUE_URL,
  DME_THERAPY_LABELS,
  type DmeMaskType,
  type DmeTherapyType,
  HCPCS_BIPAP_CODE,
  HCPCS_CPAP_CODE,
  HCPCS_SYSTEM,
  isDmeMaskType,
} from './constants';

const PRESSURE_UNIT = 'cmH2O';
const PRESSURE_SYSTEM = 'http://unitsofmeasure.org';

export interface CpapPressure {
  pressure: number;
}

export interface BipapPressure {
  ipap: number;
  epap: number;
}

export type DmePressure = CpapPressure | BipapPressure;

export function buildPressureExtension(therapy: DmeTherapyType, pressure: DmePressure): Extension {
  const parts: Extension[] = [];

  if (therapy === 'cpap' && 'pressure' in pressure) {
    parts.push(quantityExtension(DME_PRESSURE_VALUE_URL, pressure.pressure));
  } else if (therapy === 'bipap' && 'ipap' in pressure && 'epap' in pressure) {
    parts.push(quantityExtension(DME_PRESSURE_IPAP_URL, pressure.ipap));
    parts.push(quantityExtension(DME_PRESSURE_EPAP_URL, pressure.epap));
  }

  return {
    url: DME_PRESSURE_EXTENSION_URL,
    extension: parts,
  };
}

export function buildMaskExtension(mask: DmeMaskType): Extension {
  return {
    url: DME_MASK_EXTENSION_URL,
    valueCode: mask,
  };
}

export function getDmeTherapyTypeFromMedicationRequest(mr: MedicationRequest): DmeTherapyType | undefined {
  const hcpcs = mr.medicationCodeableConcept?.coding?.find((c) => c.system === HCPCS_SYSTEM);
  if (hcpcs?.code === HCPCS_CPAP_CODE) {
    return 'cpap';
  }
  if (hcpcs?.code === HCPCS_BIPAP_CODE) {
    return 'bipap';
  }
  return undefined;
}

export function readPressureFromMedicationRequest(mr: MedicationRequest): DmePressure | undefined {
  const ext = mr.extension?.find((e) => e.url === DME_PRESSURE_EXTENSION_URL);
  if (!ext?.extension) {
    return undefined;
  }
  const therapy = getDmeTherapyTypeFromMedicationRequest(mr);
  if (therapy === 'cpap') {
    const pressure = readQuantity(ext.extension, DME_PRESSURE_VALUE_URL);
    return pressure !== undefined ? { pressure } : undefined;
  }
  if (therapy === 'bipap') {
    const ipap = readQuantity(ext.extension, DME_PRESSURE_IPAP_URL);
    const epap = readQuantity(ext.extension, DME_PRESSURE_EPAP_URL);
    return ipap !== undefined && epap !== undefined ? { ipap, epap } : undefined;
  }
  return undefined;
}

export function readMaskFromMedicationRequest(mr: MedicationRequest): DmeMaskType | undefined {
  const ext = mr.extension?.find((e) => e.url === DME_MASK_EXTENSION_URL);
  return isDmeMaskType(ext?.valueCode) ? ext.valueCode : undefined;
}

export function formatPressureSummary(therapy: DmeTherapyType, pressure: DmePressure | undefined): string {
  if (!pressure) {
    return 'pressure not set';
  }
  if (therapy === 'cpap' && 'pressure' in pressure) {
    return `${formatNumber(pressure.pressure)} ${PRESSURE_UNIT}`;
  }
  if (therapy === 'bipap' && 'ipap' in pressure && 'epap' in pressure) {
    return `IPAP ${formatNumber(pressure.ipap)} / EPAP ${formatNumber(pressure.epap)} ${PRESSURE_UNIT}`;
  }
  return 'pressure not set';
}

export function formatDmeOrderDescription(mr: MedicationRequest): string {
  const therapy = getDmeTherapyTypeFromMedicationRequest(mr);
  if (!therapy) {
    return 'Submit DME order to Parachute';
  }
  const deviceLabel = DME_THERAPY_LABELS[therapy];
  // Order types that hide settings (supplies only, mask change) carry no pressure; omit it rather than print a
  // "pressure not set" placeholder.
  const pressure = readPressureFromMedicationRequest(mr);
  const pressureSummary = pressure ? formatPressureSummary(therapy, pressure) : undefined;
  const mask = readMaskFromMedicationRequest(mr);
  const maskLabel = mask ? `${DME_MASK_TYPE_LABELS[mask]} mask` : 'mask not specified';
  return `Submit DME order to Parachute: ${[deviceLabel, pressureSummary, maskLabel].filter(Boolean).join(', ')}`;
}

export function buildHcpcsCoding(therapy: DmeTherapyType): Coding {
  return therapy === 'cpap'
    ? { system: HCPCS_SYSTEM, code: HCPCS_CPAP_CODE, display: 'CPAP device' }
    : { system: HCPCS_SYSTEM, code: HCPCS_BIPAP_CODE, display: 'BiPAP device' };
}

function quantityExtension(url: string, value: number): Extension {
  return {
    url,
    valueQuantity: { value, unit: PRESSURE_UNIT, system: PRESSURE_SYSTEM, code: PRESSURE_UNIT },
  };
}

function readQuantity(extensions: Extension[], url: string): number | undefined {
  const found = extensions.find((e) => e.url === url);
  return found?.valueQuantity?.value;
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
